import { throwError } from "@/lib/throwError";
import PtsCurrentUserDetailsService from "@/services/APIs/PtsCurrentUserDetailsService";
import { del, get, post, put } from "@/services/axiosUitils";
import { DynamicPayload } from "@/utils/types/dynamicModule";

const basePath = "/formily/dynamic-model";

/**
 * Serialize params. For "ordering" pass the value as literal string so URL shows
 * ordering=["-date", "empname.firstname"] (no encoding of the value).
 */
function serializeParams(params: Record<string, unknown>): string {
  const parts: string[] = [];
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    const encodedKey = encodeURIComponent(key);
    if (key === "ordering" && typeof value === "string") {
      parts.push(`${encodedKey}=${value}`);
    } else if (Array.isArray(value)) {
      value.forEach(item => {
        parts.push(`${encodedKey}=${encodeURIComponent(String(item))}`);
      });
    } else {
      parts.push(`${encodedKey}=${encodeURIComponent(String(value))}`);
    }
  });
  return parts.join("&");
}

class DynamicModuleService {
  getGridColItems = async (
    modelName: string,
    page: number,
    pageSize: number,
    paramsFilter?: any
  ): Promise<any> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    const params = { page, page_size: pageSize, ...paramsFilter };
    const response = await get(`${basePath}/${modelName}/grid_data/`, {
      params,
      paramsSerializer: (p: Record<string, unknown>) => serializeParams(p),
    } as any);
    return response.grid_data;
  };

  getGridCoByConfig = async (
    modelName: string,
    page: number,
    pageSize: number,
    columns: string[],
    paramsFilter: any
  ): Promise<any> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    const response = await get(`${basePath}/${modelName}/`, {
      params: {
        page,
        page_size: pageSize,
        columns: JSON.stringify(columns),
        ...paramsFilter,
      },
    });
    return response;
  };

  // Get items using base endpoint with page/page_size (for PTS Task)
  getItemsByPage = async (
    modelName: string,
    page: number,
    pageSize: number,
    paramsFilter?: any
  ): Promise<any> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    const params = { page, page_size: pageSize, ...paramsFilter };
    const response = await get(`${basePath}/${modelName}/`, {
      params,
      paramsSerializer: (p: Record<string, unknown>) => serializeParams(p),
    } as any);
    return response;
  };

  getItemById = async (modelName: string, id: string): Promise<any> => {
    if (!modelName || !id) {
      throwError("Model name and ID are required", "DynamicModuleService");
    }
    return await get(`${basePath}/${modelName}/${id}/`);
  };

  createItem = async (
    modelName: string,
    payload: DynamicPayload
  ): Promise<any> => {
    if (!modelName || !payload) {
      throwError("Model name and payload are required", "DynamicModuleService");
    }
    return await post(`${basePath}/${modelName}/`, payload);
  };

  updateItem = async (
    modelName: string,
    id: string,
    payload: DynamicPayload
  ): Promise<any> => {
    if (!modelName || !id || !payload) {
      throwError(
        "Model name, ID, and payload are required",
        "DynamicModuleService"
      );
    }
    return await put(`${basePath}/${modelName}/${id}/`, payload);
  };

  deleteItem = async (modelName: string, id: string): Promise<any> => {
    if (!modelName || !id) {
      throwError("Model name and ID are required", "DynamicModuleService");
    }
    return await del(`${basePath}/${modelName}/${id}/`);
  };

  bulkDeleteItems = async (modelName: string, ids: string[]): Promise<any> => {
    if (!modelName || !ids) {
      throwError("Model name and IDs are required", "DynamicModuleService");
    }
    return await post(`${basePath}/${modelName}/bulk-delete/`, { ids });
  };

  getDropDownData = async (modelName: string): Promise<any> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    return await get(`${basePath}/${modelName}/drop_down_data/`);
  };

  getEmployeeByUserId = async (userId: string): Promise<any> => {
    if (!userId) {
      throwError("User ID is required", "DynamicModuleService");
    }

    // New API (post-login / current user): GET /pts_app/current-user-details/
    return await PtsCurrentUserDetailsService.getCurrentUserEmployee();

    // Old API (PTS Emp dynamic-model with users__eq filter) — kept for reference
    // const encodedModelName = encodeURIComponent("PTS Emp");
    // const response = await get(`${basePath}/${encodedModelName}/`, {
    //   params: {
    //     users__eq: userId,
    //   },
    // });
    // if (response && Array.isArray(response) && response.length > 0) {
    //   return response[0];
    // }
    // if (
    //   response?.results &&
    //   Array.isArray(response.results) &&
    //   response.results.length > 0
    // ) {
    //   return response.results[0];
    // }
    // return null;
  };

  getItemsByParams = async (
    modelName: string,
    paramsFilter?: any
  ): Promise<any> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    // URL encode the model name to handle spaces
    const encodedModelName = encodeURIComponent(modelName);
    const response = await get(`${basePath}/${encodedModelName}/`, {
      params: {
        ...paramsFilter,
      },
    });
    // Handle both array and object with results property
    if (Array.isArray(response)) {
      return response;
    }
    if (response?.results && Array.isArray(response.results)) {
      return response.results;
    }
    return response;
  };
}

const instance = new DynamicModuleService();
export default instance;
